import React, { useEffect, useRef, useState } from 'react';
import leagueName from '../utils/leagueName';
import { useNavigate } from 'react-router-dom';
import { getAuth, onAuthStateChanged } from 'firebase/auth';  
import DOMPurify from 'dompurify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faAlignCenter, faAlignLeft, faAlignRight, faBold, faEraser,
  faHeading, faImage, faItalic, faLink, faListOl, faListUl,
  faParagraph, faUnderline,
} from '@fortawesome/free-solid-svg-icons';
import empty from "../images/Empty.png"
import fallbackTeamLogo from "../images/worldcup.svg";
import { toPlainText } from '../utils/plainText';
import { getNationsLeagueLogoPath } from '../utils/nationsLeagueLogos';
import AdminResultCorrections from './AdminResultCorrections';
import AdminTabs from './AdminTabs';
import AdminReviewResults from './AdminReviewResults';

// Country artwork is stored separately from club artwork. These aliases match
// common odds-provider names to the filenames in public/images/worldcup.
const WORLD_CUP_LOGO_FILES = [
  'Algeria', 'Argentina', 'Australia', 'Austria', 'Belgium',
  'Bosnia and Herzegovina', 'Brazil', 'Cabo Verde', 'Canada', 'Colombia',
  'Congo DR', 'Croatia', 'Curaçao', 'Czechia', "Côte d'Ivoire", 'Ecuador',
  'Egypt', 'England', 'France', 'Germany', 'Ghana', 'Haiti', 'IR Iran',
  'Iraq', 'Japan', 'Jordan', 'Korea Republic', 'Mexico', 'Morocco',
  'Netherlands', 'New Zealand', 'Norway', 'Panama', 'Paraguay', 'Portugal',
  'Qatar', 'Saudi Arabia', 'Scotland', 'Senegal', 'South Africa', 'Spain',
  'Sweden', 'Switzerland', 'Tunisia', 'Türkiye', 'Uruguay', 'USA', 'Uzbekistan',
];

const normalizeLogoLookupKey = (name) => String(name || '')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .replace(/[øØ]/g, 'o')
  .replace(/ß/g, 'ss')
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, ' ')
  .trim();

const WORLD_CUP_LOGOS_BY_NAME = Object.fromEntries(
  WORLD_CUP_LOGO_FILES.map((filename) => [normalizeLogoLookupKey(filename), filename])
);

const WORLD_CUP_LOGO_ALIASES = {
  'bosnia herzegovina': 'Bosnia and Herzegovina',
  'cape verde': 'Cabo Verde',
  'democratic republic of congo': 'Congo DR',
  'dr congo': 'Congo DR',
  'ivory coast': "Côte d'Ivoire",
  'iran': 'IR Iran',
  'south korea': 'Korea Republic',
  'republic of korea': 'Korea Republic',
  'turkey': 'Türkiye',
  'united states': 'USA',
  'united states of america': 'USA',
};

const normalizeClubLogoName = (teamName) => String(teamName || '')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .replace(/[øØ]/g, 'o')
  .replace(/ß/g, 'ss')
  .replace(/[łŁ]/g, (character) => character === 'Ł' ? 'L' : 'l')
  .replace(/[-/]/g, '_')
  .replace(/\.\s+/g, '_')
  .replace(/\s+/g, '_')
  .replace(/\.+$/, '');

export default function PendingBetsAdmin( {user, setUser, setRole, showAlert, setShowAlert, alertText, setAlertText, animationClass, setAnimationClass} ) {
  const [pendingBets, setPendingBets] = useState({});
  const [selectedWinners, setSelectedWinners] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [savedMarkets, setSavedMarkets] = useState({});
  const [marketingSubject, setMarketingSubject] = useState('');
  const [marketingMessage, setMarketingMessage] = useState('');
  const [marketingRecipientCount, setMarketingRecipientCount] = useState(0);
  const [marketingCampaigns, setMarketingCampaigns] = useState([]);
  const [marketingEnabled, setMarketingEnabled] = useState(false);
  const [marketingConfigured, setMarketingConfigured] = useState(true);
  const [marketingNotice, setMarketingNotice] = useState(null);
  const [marketingLoading, setMarketingLoading] = useState(false);
  const [marketingPreviewOpen, setMarketingPreviewOpen] = useState(false);
  const [marketingPreviewDevice, setMarketingPreviewDevice] = useState('desktop');
  const [marketingImageSelected, setMarketingImageSelected] = useState(false);
  const [adminTab, setAdminTab] = useState('pending');
  const [pendingRevision, setPendingRevision] = useState(0);
  const [matchScores, setMatchScores] = useState({ loading: true, results: {} });
  const marketingEditorRef = useRef(null);
  const selectedMarketingImageRef = useRef(null);
  const navigate = useNavigate();

  const [confirmAction, setConfirmAction] = useState(null); 
  // { type: 'save' | 'void', payload: { match_day, time, fixture, bet_market } }

  useEffect(() => {
    if (!user || adminTab !== 'corrections') return undefined;
    const controller = new AbortController();
    setMatchScores({ loading: true, results: {} });
    (async () => {
      try {
        const token = await user.getIdToken();
        if (controller.signal.aborted) return;
        const response = await fetch('/api/admin/match-scores', {
          headers: { Authorization: `Bearer ${token}` }, signal: controller.signal,
        });
        const data = await response.json();
        if (!response.ok || !data.success) throw new Error('Unable to load API results. Bets have not been changed.');
        if (!controller.signal.aborted) setMatchScores({ ...data, loading: false });
      } catch (_) {
        if (!controller.signal.aborted) setMatchScores({ loading: false, results: {}, error: 'Unable to load API results. Bets have not been changed.' });
      }
    })();
    return () => controller.abort();
  }, [user, adminTab]);

  


  useEffect(() => {
     const fetchUserData = async (currentUser) => {
       if (!currentUser) return; 
   
       try {
        const token = await currentUser.getIdToken(); 

        const response = await fetch('/api/user-info', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}` 
          },
          body: JSON.stringify({ currentUser }),
        });

        const data = await response.json();
        // console.log({data})
        setRole(data.user.admin ?? false); 
        if (!(data.user.admin ?? false)){
          navigate("/");
        }
       } catch (error) {
          console.error("Error getting user info:", error);
          navigate("/");
       }
     };
   
     const auth = getAuth();
     const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
       if (currentUser) {
         setUser(currentUser);
         // console.log(currentUser);
         await fetchUserData(currentUser);
       } else {
         setUser(null);
         setRole(false); 
       }
     });
   
     return () => unsubscribe();
   }, []);

  useEffect(() => {
      if (!user || adminTab !== 'pending') return;
      const controller = new AbortController();
      const fetchPendingBets = async () => {
          try {
            const token = await user.getIdToken(); 
            if (controller.signal.aborted) return;
            const response = await fetch('/api/pending-bets', {
              method: 'POST',
              signal: controller.signal,
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}` 
              },
              body: JSON.stringify({ user }),
            });
            const data = await response.json();
            if (controller.signal.aborted) return;
            // console.log(data)
            if (data.success) {
              // console.log(data.data)
              setPendingBets(data.data);
              setSavedMarkets({});
              setSelectedWinners({});
            } else {
              alert(data.error)
            }
          } catch (error) {
              if (controller.signal.aborted) return;
              console.error("Error fetching pending bets:", error);
          }
      };
      fetchPendingBets();
      return () => controller.abort();
  }, [user, pendingRevision, adminTab]);

  // Refresh campaign progress while the admin page is open. This reads only
  // totals and never downloads the recipient email list into the browser.
  useEffect(() => {
    if (!user) return undefined;
    let cancelled = false;

    const fetchMarketingSummary = async () => {
      try {
        const token = await user.getIdToken();
        const response = await fetch('/api/admin/marketing-summary', {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.error || 'Unable to load marketing emails.');
        if (!cancelled) {
          setMarketingRecipientCount(data.recipientCount);
          setMarketingCampaigns(data.campaigns || []);
          setMarketingEnabled(Boolean(data.enabled));
          setMarketingConfigured(data.configured);
        }
      } catch (error) {
        if (!cancelled) setMarketingNotice({ type: 'danger', text: error.message });
      }
    };

    fetchMarketingSummary();
    const interval = setInterval(fetchMarketingSummary, 5000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [user]);

  const sendMarketingCampaign = async () => {
    setMarketingLoading(true);
    setMarketingNotice(null);
    try {
      const token = await user.getIdToken();
      const response = await fetch('/api/admin/marketing-campaigns', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ subject: marketingSubject, messageHtml: marketingMessage }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Unable to queue the campaign.');

      setMarketingPreviewOpen(false);
      setMarketingSubject('');
      setMarketingMessage('');
      if (marketingEditorRef.current) marketingEditorRef.current.innerHTML = '';
      selectedMarketingImageRef.current = null;
      setMarketingImageSelected(false);
      setMarketingCampaigns((campaigns) => [{
        campaign_id: data.campaignId,
        subject: marketingSubject.trim(),
        status: data.status,
        recipient_count: data.recipientCount,
        sent_count: 0,
        failed_count: 0,
        skipped_count: 0,
        created_at: new Date().toISOString(),
      }, ...campaigns].slice(0, 10));
      setMarketingNotice({
        type: 'success',
        text: `Campaign queued for ${data.recipientCount} opted-in recipient${data.recipientCount === 1 ? '' : 's'}.`,
      });
    } catch (error) {
      setMarketingNotice({ type: 'danger', text: error.message });
    } finally {
      setMarketingLoading(false);
    }
  };

  function isEmpty(obj) {
    for (const prop in obj) {
      if (Object.hasOwn(obj, prop)) {
        return false;
      }
    }
    return true;
  }

  const handleCheckboxChange = (match_day, fixture, bet_market, bet_type) => {
    // console.log(selectedWinners)
    setSelectedWinners((prev) => ({
        ...prev,
        [match_day]: {
            ...prev[match_day],
            [fixture]: {
                ...prev[match_day]?.[fixture],
                [bet_market]: {
                    ...prev[match_day]?.[fixture]?.[bet_market],
                    [bet_type]: !prev[match_day]?.[fixture]?.[bet_market]?.[bet_type]
                }
            }
        }
    }));
  };
  const submitMarketResults = async (match_day, time, fixture, bet_market) => {
    // Check if already saved or currently saving
    const isAlreadySaving =
      savedMarkets?.[match_day]?.[fixture]?.[bet_market];

    if (isAlreadySaving) {
      // console.log("Already saving this market, ignoring duplicate click.");
      return; // Ignore further clicks
    }

    // Immediately disable the button to prevent multiple clicks
    setSavedMarkets((prev) => ({
      ...prev,
      [match_day]: {
        ...(prev[match_day] || {}),
        [fixture]: {
          ...(prev[match_day]?.[fixture] || {}),
          [bet_market]: true, // temporarily mark as saving
        },
      },
    }));

    try {
      await saveMarketResults(match_day, time, fixture, bet_market);
      // Keep it marked as saved (success)
    } catch (error) {
      console.error("Error saving market results:", error);
      // Revert the saved flag if the request fails
      setSavedMarkets((prev) => ({
        ...prev,
        [match_day]: {
          ...(prev[match_day] || {}),
          [fixture]: {
            ...(prev[match_day]?.[fixture] || {}),
            [bet_market]: false,
          },
        },
      }));
    }
  };

  const saveMarketResults = async (match_day, time, fixture, bet_market) => {
      const winningBets = Object.entries(selectedWinners[match_day]?.[fixture]?.[bet_market] || {})
          .filter(([_, isChecked]) => isChecked)
          .map(([bet_type]) => bet_type);

      try {
          const token = await user.getIdToken(); 
          const response = await fetch('/api/update-bet-results', {
              method: 'POST',
              headers: { 
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
              },
              body: JSON.stringify({ match_day, time, fixture, bet_market, winningBets })
          });

          const result = await response.json();
          if (result.success) {
              setAlertText(`<strong>Saved results successfully! </strong>`);
              setShowAlert(true);

              const fadeOutTimeout = setTimeout(() => {
                setAnimationClass('alert-fade-out');
              }, 2000);

              const clearAlertTimeout = setTimeout(() => {
                setShowAlert(false);
                setAnimationClass('');
              }, 2500);

              return () => {
                clearTimeout(fadeOutTimeout);
                clearTimeout(clearAlertTimeout);
              };
          } else {
              throw new Error(result.error || 'Unable to save results.');
          }
      } catch (error) {
          console.error("Error updating results:", error);
          alert(error.message);
          throw error;
      }
  };

  const voidMarket = async (match_day, time, fixture, bet_market) => {
    // Immediately disable the button to prevent multiple clicks
    setSavedMarkets((prev) => ({
      ...prev,
      [match_day]: {
        ...(prev[match_day] || {}),
        [fixture]: {
          ...(prev[match_day]?.[fixture] || {}),
          [bet_market]: true, // temporarily mark as saving
        },
      },
    }));
    try {
      const token = await user.getIdToken();
      const response = await fetch('/api/void-bet-market', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ match_day, time, fixture, bet_market }),
      });

      const result = await response.json();
      if (!response.ok || !result.success) throw new Error(result.error || 'Unable to void market.');
      if (result.success) {
        setAlertText(`<strong>Saved results successfully! </strong>`);
        setShowAlert(true);

        const fadeOutTimeout = setTimeout(() => {
          setAnimationClass('alert-fade-out');
        }, 2000);

        const clearAlertTimeout = setTimeout(() => {
          setShowAlert(false);
          setAnimationClass('');
        }, 2500);
        
         // Mark as 'saved' to disable further actions
        setSavedMarkets(prev => ({
          ...prev,
          [match_day]: {
            ...(prev[match_day] || {}),
            [fixture]: {
              ...(prev[match_day]?.[fixture] || {}),
              [bet_market]: true,
            }
          }
        }));

        return () => {
          clearTimeout(fadeOutTimeout);
          clearTimeout(clearAlertTimeout);
        };

       
      }
    } catch (error) {
      console.error("Error voiding market:", error);
      alert(error.message);
      setSavedMarkets((prev) => ({
        ...prev,
        [match_day]: {
          ...prev[match_day],
          [fixture]: { ...prev[match_day]?.[fixture], [bet_market]: false },
        },
      }));
    }
  };


  // Full mapping from full name → short name
  const teamMap = {
    // Premier League
    "Tottenham Hotspur": "tottenham",
    "West Ham United": "west-ham",
    "Fulham": "fulham",
    "Aston Villa": "aston-villa",
    "Manchester United": "man-utd",
    "Brentford": "brentford",
    "Newcastle United": "newcastle",
    "Brighton and Hove Albion": "brighton",
    "Ipswich Town": "ipswich",
    "Everton": "everton",
    "Southampton": "southampton",
    "Leicester City": "leicester",
    "Bournemouth": "bournemouth",
    "Arsenal": "arsenal",
    "Wolverhampton Wanderers": "wolverhampton",
    "Manchester City": "man-city",
    "Liverpool": "liverpool",
    "Chelsea": "chelsea",
    "Nottingham Forest": "nottingham-forest",
    "Crystal Palace": "crystal-palace",
    "Burnley": "burnley",
    "Leeds United": "leeds",
    "Sunderland": "sunderland",

    // Ligue 1
    "AS Monaco": "monaco",
    "Lorient": "lorient",
    "Toulouse": "toulouse",
    "Nantes": "nantes",
    "Paris Saint Germain": "psg",
    "Auxerre": "auxerre",
    "Nice": "nice",
    "Paris FC": "paris-fc",
    "Angers": "angers",
    "Brest": "brest",
    "Metz": "metz",
    "Le Havre": "le-havre",
    "Lille": "lille",
    "Lyon": "lyon",
    "Rennes": "rennes",
    "RC Lens": "lens",
    "Marseille": "marseille",
    "Strasbourg": "strasbourg",

    // Bundesliga
    "Bayern Munich": "bayern-munich",
    "Werder Bremen": "werder-bremen",
    "1 FC Heidenheim": "heidenheim",
    "Augsburg": "augsburg",
    "FSV Mainz 05": "mainz",
    "Borussia Dortmund": "borussia-dortmund",
    "FC St Pauli": "st-pauli",
    "Bayer Leverkusen": "bayer-leverkusen",
    "VfL Wolfsburg": "wolfsburg",
    "RB Leipzig": "rb-leipzig",
    "Borussia Monchengladbach": "borussia-mgladbach",
    "Eintracht Frankfurt": "eintracht-frankfurt",
    "SC Freiburg": "sc-freiburg",
    "TSG Hoffenheim": "tsg-hoffenheim",
    "1 FC Koln": "cologne",
    "VfB Stuttgart": "vfb-stuttgart",
    "Union Berlin": "union-berlin",
    "Hamburger SV": "hamburg",

    // Serie A
    "AS Roma": "roma",
    "Hellas Verona": "verona",
    "AC Milan": "ac-milan",
    "Napoli": "napoli",
    "Como": "como",
    "Cremonese": "cremonese",
    "Genoa": "genoa",
    "Lazio": "lazio",
    "Lecce": "lecce",
    "Bologna": "bologna",
    "Parma": "parma",
    "Torino": "torino",
    "Pisa": "pisa",
    "Fiorentina": "fiorentina",
    "Sassuolo": "sassuolo",
    "Udinese": "udinese",
    "Cagliari": "cagliari",
    "Inter Milan": "inter-milan",
    "Atalanta BC": "atalanta",
    "Juventus": "juventus",

    // La Liga
    "Girona": "girona",
    "Espanyol": "espanyol",
    "Getafe": "getafe",
    "Levante": "levante",
    "Mallorca": "real-mallorca",
    "Alaves": "alaves",
    "Rayo Vallecano": "rayo-vallecano",
    "Sevilla": "sevilla",
    "Barcelona": "barcelona",
    "Real Sociedad": "real-sociedad",
    "Valencia": "valencia",
    "Oviedo": "oviedo",
    "Atletico Madrid": "atletico-madrid",
    "Real Madrid": "real-madrid",
    "Villarreal": "villarreal",
    "Athletic Bilbao": "athletic-bilbao",
    "Elche CF": "elche",
    "Celta Vigo": "celta-vigo",
    "Real Betis": "real-betis",
    "CA Osasuna": "osasuna",

    // Champions League (additional teams)
    "Club Brugge": "club-brugge",
    "FC Kairat": "kairat-almaty",
    "Pafos FC": "aep-paphos",
    "Bodo Glimt": "bodo-glimt",
    "Benfica": "benfica",
    "Galatasaray": "galatasaray",
    "Slavia Praha": "slavia-prague",
    "Ajax": "ajax",
    "Qarabag FK": "fk-qarabag",
    "FC Copenhagen": "fc-copenhagen",
    "Union Saint Gilloise": "union-st-gilloise",
    "Olympiakos Piraeus": "olympiakos",
    "PSV Eindhoven": "psv",
    "Sporting Lisbon": "sporting-lisbon"
  };

  // Reverse mapping: shortName => full name
  const reverseTeamMap = Object.fromEntries(
    Object.entries(teamMap).map(([full, short]) => [short, full])
  );
  const getFullTeamName = (shortName) => reverseTeamMap[shortName] || "";

  const getImagePath = (teamName) => {
    const shortName = String(teamName || '').replace(/ /g, '-').toLowerCase();
    // Old stored fixtures use short names such as "Man City". New fixtures
    // may already contain the full provider name, so never replace them with
    // an empty string when no abbreviation mapping exists.
    const resolvedName = getFullTeamName(shortName) || teamName;
    const nationsLeagueLogo = getNationsLeagueLogoPath(resolvedName);
    if (nationsLeagueLogo) return nationsLeagueLogo;
    const countryLookupKey = normalizeLogoLookupKey(resolvedName);
    const countryFilename = WORLD_CUP_LOGO_ALIASES[countryLookupKey]
      || WORLD_CUP_LOGOS_BY_NAME[countryLookupKey];

    if (countryFilename) {
      return `/images/worldcup/${encodeURIComponent(countryFilename)}.png`;
    }

    return `/images/${normalizeClubLogoName(resolvedName)}.png`;
  };

  const updateMarketingMessage = () => {
    setMarketingMessage(marketingEditorRef.current?.innerHTML || '');
  };

  const applyMarketingFormat = (command, value = null) => {
    marketingEditorRef.current?.focus();
    document.execCommand(command, false, value);
    updateMarketingMessage();
  };

  const alignMarketingText = (command, alignment) => {
    marketingEditorRef.current?.focus();
    document.execCommand(command, false);
    marketingEditorRef.current
      ?.querySelectorAll('[style*="text-align"], [align]')
      .forEach((element) => {
        const appliedAlignment = element.style.textAlign || element.getAttribute('align');
        if (['left', 'center', 'right'].includes(appliedAlignment)) {
          element.dataset.textAlign = alignment;
        }
        element.removeAttribute('style');
        element.removeAttribute('align');
      });
    updateMarketingMessage();
  };

  const addMarketingLink = (buttonStyle) => {
    const selection = window.getSelection();
    const selectedText = selection?.toString().trim() || '';
    const savedRange = selection?.rangeCount ? selection.getRangeAt(0).cloneRange() : null;
    const label = window.prompt('Enter the text shown on the button', selectedText || 'Learn more');
    if (!label?.trim()) return;
    const url = window.prompt('Enter the full destination link, including https://');
    if (!url) return;
    try {
      const parsed = new URL(url);
      if (!['http:', 'https:', 'mailto:'].includes(parsed.protocol)) throw new Error();

      const editor = marketingEditorRef.current;
      editor?.focus();
      const range = savedRange && editor?.contains(savedRange.commonAncestorContainer)
        ? savedRange
        : document.createRange();
      if (!savedRange || !editor?.contains(savedRange.commonAncestorContainer)) {
        range.selectNodeContents(editor);
        range.collapse(false);
      }

      range.deleteContents();
      const link = document.createElement('a');
      link.href = parsed.href;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      link.dataset.buttonStyle = buttonStyle;
      link.className = `marketing-cta marketing-cta-${buttonStyle}`;
      link.textContent = label.trim();
      range.insertNode(link);
      range.setStartAfter(link);
      range.collapse(true);
      selection.removeAllRanges();
      selection.addRange(range);
      updateMarketingMessage();
    } catch {
      setMarketingNotice({ type: 'danger', text: 'Enter a valid HTTP, HTTPS, or email link.' });
    }
  };

  const addMarketingImage = () => {
    const url = window.prompt('Enter the public HTTPS image URL');
    if (!url) return;
    try {
      const parsed = new URL(url);
      if (parsed.protocol !== 'https:') throw new Error();
      applyMarketingFormat('insertImage', parsed.href);
      const insertedImages = Array.from(marketingEditorRef.current?.querySelectorAll('img') || []);
      const insertedImage = insertedImages.find((image) => !image.dataset.size);
      if (insertedImage) {
        insertedImage.dataset.size = '100';
        insertedImage.dataset.align = 'center';
        selectedMarketingImageRef.current = insertedImage;
        setMarketingImageSelected(true);
        updateMarketingMessage();
      }
    } catch {
      setMarketingNotice({ type: 'danger', text: 'Images must use a valid public HTTPS URL.' });
    }
  };

  const selectMarketingImage = (event) => {
    const image = event.target.closest?.('img');
    if (selectedMarketingImageRef.current) {
      selectedMarketingImageRef.current.classList.remove('marketing-image-selected');
    }
    selectedMarketingImageRef.current = image || null;
    if (image) {
      image.dataset.size ||= '100';
      image.dataset.align ||= 'center';
      image.classList.add('marketing-image-selected');
    }
    setMarketingImageSelected(Boolean(image));
  };

  const formatMarketingImage = ({ size, align }) => {
    const image = selectedMarketingImageRef.current;
    if (!image || !marketingEditorRef.current?.contains(image)) {
      setMarketingImageSelected(false);
      return;
    }
    if (size) image.dataset.size = size;
    if (align) image.dataset.align = align;
    updateMarketingMessage();
  };

  const cleanMarketingPreview = DOMPurify.sanitize(marketingMessage, {
    ALLOWED_TAGS: [
      'p', 'br', 'strong', 'b', 'em', 'i', 'u', 'h1', 'h2', 'h3',
      'ul', 'ol', 'li', 'blockquote', 'div', 'a', 'img',
    ],
    ALLOWED_ATTR: ['href', 'target', 'rel', 'data-button-style', 'class', 'src', 'alt', 'data-size', 'data-align', 'data-text-align'],
    ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto):)/i,
  });
  const marketingTextLength = toPlainText(cleanMarketingPreview).length;

  const handleTeamLogoError = (event) => {
    // Avoid a broken-image icon when a new team has not been added yet.
    event.currentTarget.onerror = null;
    event.currentTarget.src = fallbackTeamLogo;
  };

  const winningList = confirmAction?.payload?.selectedTypes?.length > 0
    ? confirmAction.payload.selectedTypes.join(", ")
    : "None";

  return (
    <div>
      <div className='col-lg-10 col-sm-12 m-auto'>
        <AdminTabs selected={adminTab} onChange={setAdminTab} />
      </div>

      {adminTab === 'marketing' && (
      <div className='col-lg-10 col-sm-12 m-auto'>
        <div className='bg-green shadow-down rounded p-4 mb-5 text-white text-start match-list admin-tab-panel'>
          <div className='d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2'>
            <div>
              <div className='fw-bold'>{marketingRecipientCount} opted-in recipient{marketingRecipientCount === 1 ? '' : 's'}</div>
              <div className='font-12 text-lightgrey'>Each person receives a separate email. Addresses are never shared.</div>
            </div>
            {!marketingEnabled ? (
              <span className='badge bg-secondary'>Sending temporarily disabled</span>
            ) : !marketingConfigured && (
              <span className='badge bg-warning text-dark'>Brevo setup required</span>
            )}
          </div>

          {!marketingEnabled && (
            <div className='alert alert-info' role='status'>
              Marketing consent is still being collected. You can compose and preview emails, but delivery is disabled until the mailbox is ready.
            </div>
          )}

          {marketingNotice && (
            <div className={`alert alert-${marketingNotice.type}`} role='alert'>
              {marketingNotice.text}
            </div>
          )}

          <label className='form-label fw-bold' htmlFor='marketing-subject'>Subject</label>
          <input
            id='marketing-subject'
            className='form-control mb-3'
            maxLength='150'
            value={marketingSubject}
            onChange={(event) => setMarketingSubject(event.target.value)}
            placeholder='Email subject'
          />

          <label className='form-label fw-bold' htmlFor='marketing-message'>Message</label>
          <div className='marketing-editor-toolbar d-flex flex-wrap gap-1 p-2 rounded-top' role='toolbar' aria-label='Email formatting'>
            <button id='marketing-format-bold' type='button' className='btn btn-sm btn-dark marketing-format-button' title='Bold' aria-label='Bold' onMouseDown={(event) => event.preventDefault()} onClick={() => applyMarketingFormat('bold')}><FontAwesomeIcon icon={faBold} /></button>
            <button id='marketing-format-italic' type='button' className='btn btn-sm btn-dark marketing-format-button' title='Italic' aria-label='Italic' onMouseDown={(event) => event.preventDefault()} onClick={() => applyMarketingFormat('italic')}><FontAwesomeIcon icon={faItalic} /></button>
            <button id='marketing-format-underline' type='button' className='btn btn-sm btn-dark marketing-format-button' title='Underline' aria-label='Underline' onMouseDown={(event) => event.preventDefault()} onClick={() => applyMarketingFormat('underline')}><FontAwesomeIcon icon={faUnderline} /></button>
            <button id='marketing-format-heading' type='button' className='btn btn-sm btn-dark marketing-format-button' title='Heading' aria-label='Heading' onMouseDown={(event) => event.preventDefault()} onClick={() => applyMarketingFormat('formatBlock', 'h2')}><FontAwesomeIcon icon={faHeading} /></button>
            <button id='marketing-format-paragraph' type='button' className='btn btn-sm btn-dark marketing-format-button' title='Paragraph' aria-label='Paragraph' onMouseDown={(event) => event.preventDefault()} onClick={() => applyMarketingFormat('formatBlock', 'p')}><FontAwesomeIcon icon={faParagraph} /></button>
            <button id='marketing-format-bullets' type='button' className='btn btn-sm btn-dark marketing-format-button' title='Bulleted list' aria-label='Bulleted list' onMouseDown={(event) => event.preventDefault()} onClick={() => applyMarketingFormat('insertUnorderedList')}><FontAwesomeIcon icon={faListUl} /></button>
            <button id='marketing-format-numbers' type='button' className='btn btn-sm btn-dark marketing-format-button' title='Numbered list' aria-label='Numbered list' onMouseDown={(event) => event.preventDefault()} onClick={() => applyMarketingFormat('insertOrderedList')}><FontAwesomeIcon icon={faListOl} /></button>
            <button id='marketing-align-left' type='button' className='btn btn-sm btn-dark marketing-format-button' title='Align text left' aria-label='Align text left' onMouseDown={(event) => event.preventDefault()} onClick={() => alignMarketingText('justifyLeft', 'left')}><FontAwesomeIcon icon={faAlignLeft} /></button>
            <button id='marketing-align-center' type='button' className='btn btn-sm btn-dark marketing-format-button' title='Align text centre' aria-label='Align text centre' onMouseDown={(event) => event.preventDefault()} onClick={() => alignMarketingText('justifyCenter', 'center')}><FontAwesomeIcon icon={faAlignCenter} /></button>
            <button id='marketing-align-right' type='button' className='btn btn-sm btn-dark marketing-format-button' title='Align text right' aria-label='Align text right' onMouseDown={(event) => event.preventDefault()} onClick={() => alignMarketingText('justifyRight', 'right')}><FontAwesomeIcon icon={faAlignRight} /></button>
            <button id='marketing-insert-image' type='button' className='btn btn-sm btn-dark marketing-format-button' title='Insert image' aria-label='Insert image' onMouseDown={(event) => event.preventDefault()} onClick={addMarketingImage}><FontAwesomeIcon icon={faImage} /></button>
            <button id='marketing-clear-format' type='button' className='btn btn-sm btn-dark marketing-format-button' title='Clear formatting' aria-label='Clear formatting' onMouseDown={(event) => event.preventDefault()} onClick={() => applyMarketingFormat('removeFormat')}><FontAwesomeIcon icon={faEraser} /></button>
            <span className='marketing-toolbar-divider' aria-hidden='true'></span>
            <button id='marketing-button-primary' type='button' className='btn btn-sm marketing-format-button marketing-insert-cta marketing-insert-cta-primary' title='Insert primary button link' aria-label='Insert primary button link' onMouseDown={(event) => event.preventDefault()} onClick={() => addMarketingLink('primary')}><FontAwesomeIcon icon={faLink} /><span className='ms-1'>1</span></button>
            <button id='marketing-button-secondary' type='button' className='btn btn-sm marketing-format-button marketing-insert-cta marketing-insert-cta-secondary' title='Insert secondary button link' aria-label='Insert secondary button link' onMouseDown={(event) => event.preventDefault()} onClick={() => addMarketingLink('secondary')}><FontAwesomeIcon icon={faLink} /><span className='ms-1'>2</span></button>
            <button id='marketing-button-tertiary' type='button' className='btn btn-sm marketing-format-button marketing-insert-cta marketing-insert-cta-tertiary' title='Insert tertiary button link' aria-label='Insert tertiary button link' onMouseDown={(event) => event.preventDefault()} onClick={() => addMarketingLink('tertiary')}><FontAwesomeIcon icon={faLink} /><span className='ms-1'>3</span></button>
          </div>
          {marketingImageSelected && (
            <div className='marketing-image-toolbar d-flex flex-wrap align-items-center gap-1 px-2 py-2'>
              <span className='font-12 me-1'>Selected image:</span>
              <button type='button' className='btn btn-sm btn-outline-light' onClick={() => formatMarketingImage({ size: '25' })}>25%</button>
              <button type='button' className='btn btn-sm btn-outline-light' onClick={() => formatMarketingImage({ size: '50' })}>50%</button>
              <button type='button' className='btn btn-sm btn-outline-light' onClick={() => formatMarketingImage({ size: '100' })}>100%</button>
              <button type='button' className='btn btn-sm btn-outline-light' onClick={() => formatMarketingImage({ align: 'left' })}>Left</button>
              <button type='button' className='btn btn-sm btn-outline-light' onClick={() => formatMarketingImage({ align: 'center' })}>Centre</button>
              <button type='button' className='btn btn-sm btn-outline-light' onClick={() => formatMarketingImage({ align: 'right' })}>Right</button>
            </div>
          )}
          <div
            id='marketing-message'
            ref={marketingEditorRef}
            className='form-control marketing-rich-editor rounded-top-0 mb-2'
            contentEditable
            suppressContentEditableWarning
            onInput={updateMarketingMessage}
            onClick={selectMarketingImage}
            data-placeholder='Write and format your email here...'
            role='textbox'
            aria-multiline='true'
          />
          <div className='font-12 text-lightgrey mb-3'>
            {marketingTextLength.toLocaleString()} text characters. Insert an image at the cursor, then click it to resize or align it. You can drag it to another position. An unsubscribe link is added automatically.
          </div>
          <button
            className='btn btn-prim text-white fw-bold px-4'
            disabled={
              marketingLoading ||
              !marketingSubject.trim() ||
              !marketingTextLength
            }
            onClick={() => setMarketingPreviewOpen(true)}
          >
            Preview email
          </button>

          <h2 className='h5 mt-5 mb-3'>Recent campaigns</h2>
          {marketingCampaigns.length === 0 ? (
            <p className='text-lightgrey mb-0'>No marketing campaigns have been created.</p>
          ) : (
            <div className='tableWrap marketing-campaign-table-wrap'>
              <table size='sm' className='transactions marketing-campaign-table'>
                <thead>
                  <tr>
                    <th>Subject</th>
                    <th>Status</th>
                    <th>Recipients</th>
                    <th>Sent</th>
                    <th>Failed</th>
                    <th>Skipped</th>
                    <th aria-label='Status indicator'></th>
                  </tr>
                </thead>
                <tbody>
                  {marketingCampaigns.map((campaign) => (
                    <tr key={campaign.campaign_id}>
                      <td>{campaign.subject}</td>
                      <td className='text-capitalize'>{campaign.status}</td>
                      <td>{campaign.recipient_count}</td>
                      <td>{campaign.sent_count}</td>
                      <td>{campaign.failed_count}</td>
                      <td>{campaign.skipped_count}</td>
                      <td className='badges'>
                        <span
                          className={`badge ${
                            campaign.status === 'completed'
                              ? 'bg-success'
                              : campaign.status === 'failed'
                                ? 'bg-danger'
                                : campaign.status === 'partial'
                                  ? 'bg-warning'
                                  : campaign.status === 'sending'
                                    ? 'bg-info'
                                    : 'bg-secondary'
                          }`}
                          title={campaign.status}
                          aria-label={campaign.status}
                        >
                          &nbsp;
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
      )}

      {adminTab === 'corrections' && <AdminResultCorrections user={user} scores={matchScores} />}
      {adminTab === 'review' && <AdminReviewResults user={user} onSettled={() => {
        setPendingRevision((value) => value + 1);
        setSavedMarkets({});
        setSelectedWinners({});
      }} />}
      {adminTab === 'pending' && (
      <div className='col-lg-10 col-sm-12 m-auto'>
        <div className='bg-green shadow-down rounded pb-3 mb-5 match-list admin-tab-panel'>
          <div className='col-12'>
            <div className='col-12 row d-flex align-items-center justify-content-between'>
              <div className="col-8 searchgroup text-end d-flex align-items-center">
                <span className="searchgroup-text" id="basic-addon1">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" 
                      fill="currentColor" className="bi bi-search" viewBox="0 0 16 16">
                      <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001q.044.06.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1 1 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0"/>
                    </svg>
                  </span>
                <input type="text" className='search' placeholder="Search team..." onChange={(e) => setSearchTerm(e.target.value.toLowerCase())} value={searchTerm} />
              </div>
              <div className='col-4 toggle d-flex justify-content-end align-items-center m-0 text-lightgrey pe-0 font-12 fw-bold' data-bs-toggle="tooltip" data-bs-placement="top" title="Change to odds to %">
        
              </div>
            </div>
            <div>
            { !isEmpty(pendingBets) ? (
              Object.entries(pendingBets).map(([match_day, fixtures]) => (
                <div key={match_day} className="date-group">
                  <div className='text-uppercase text-lightgrey col-12 bg-lightgreen text-start date matchMargin py-2'>
                  {formatLocalDate(match_day, Object.values(fixtures)[0]?.time)}
                  </div>
                  {Object.entries(fixtures)
                        .filter(([fixture]) => fixture.toLowerCase().includes(searchTerm)) // Filter fixtures based on search
                        .map(([fixture, details]) => (
                    <div key={fixture} className='match'>
                      <div className='matchMargin text-start'>
                      <div className='text-lightgrey py-2 font-12 d-flex justify-content-between align-items-center'>
                        <div className='d-flex justify-content-start py-1 text-white fw-bold font-15 align-items-center'>

                          {/* HOME TEAM */}
                          <div className="d-flex align-items-center team-wrap">
                            <img 
                              src={getImagePath(fixture.split(" v ")[0])}
                              alt={fixture.split(" v ")[0]}
                              className="team-logo me-1"
                              onError={handleTeamLogoError}
                            />
                            <span>{fixture.split(" v ")[0]}</span>
                          </div>

                          <div className='mx-3'> - </div>

                          {/* AWAY TEAM */}
                          <div className="d-flex align-items-center team-wrap">
                            <span>{fixture.split(" v ")[1]}</span>
                            <img 
                              src={getImagePath(fixture.split(" v ")[1])}
                              alt={fixture.split(" v ")[1]}
                              className="team-logo ms-1"
                              onError={handleTeamLogoError}
                            />
                          </div>

                        </div>

                        <div className='text-end'>
                          <div className='small text-lightgrey'>{leagueName(details.sport_keys)}</div>
                          <div>{formatLocalTime(match_day, details.time)}</div>
                        </div>
                      </div> 
                      <div className="row">

                      {Object.entries(details.markets).map(([bet_market, bet_types]) => {
                      const isSaved = savedMarkets[match_day]?.[fixture]?.[bet_market] || false;
                      return (
                        <div key={bet_market} className="col-12 col-sm-6 col-md-6 col-lg-3 mb-3">
                          <div className="bet-card p-3 shadow-sm">
                            <div className='d-flex justify-content-between align-items-center'>
                              <div className='text-white font-15'>{bet_market}</div>
                                <div>
                                  {/* SAVE Button */}
                                  <div
                                    onClick={() => {
                                      const selectedTypesArray = Object.entries(selectedWinners[match_day]?.[fixture]?.[bet_market] || {})
                                        .filter(([_, isSelected]) => isSelected)
                                        .map(([type]) => type);

                                        return !isSaved && setConfirmAction({
                                        type: 'save',
                                        payload: { match_day, 'time': details.time, fixture, bet_market, selectedTypes: selectedTypesArray }
                                      });
                                    }}
                                    className={`mb-1 rounded px-2 py-1 text-center oddsClicked odds-content fw-bold ${isSaved ? 'text-muted' : 'text-blue'}`}
                                    style={{ cursor: isSaved ? 'not-allowed' : 'pointer', opacity: isSaved ? 0.5 : 1 }}
                                  >
                                    {isSaved ? "SAVED" : "SAVE"}
                                  </div>

                                  {/* VOID Button */}
                                  <div
                                    onClick={() => {
                                      const selectedTypesArray = Object.entries(selectedWinners[match_day]?.[fixture]?.[bet_market] || {})
                                        .filter(([_, isSelected]) => isSelected)
                                        .map(([type]) => type);

                                      return !isSaved && setConfirmAction({
                                        type: 'void',
                                        payload: { match_day, 'time': details.time, fixture, bet_market, selectedTypes: selectedTypesArray }
                                      });
                                    }}
                                    className={`mt-1 rounded px-2 py-1 text-center fw-bold ${isSaved ? 'text-muted' : 'bg-danger text-white'}`}
                                    style={{ cursor: isSaved ? 'not-allowed' : 'pointer', opacity: isSaved ? 0.5 : 1 }}
                                  >
                                    VOID
                                  </div>
                              </div>
                            </div>

                            {bet_types.map((bet_type) => (
                              <label key={bet_type} className='d-flex align-items-center font-12 text-grey fw-normal my-2'>
                                <input
                                  type="checkbox"
                                  checked={selectedWinners[match_day]?.[fixture]?.[bet_market]?.[bet_type] || false}
                                  onChange={() => handleCheckboxChange(match_day, fixture, bet_market, bet_type)}
                                  className='me-2'
                                  disabled={isSaved} 
                                />
                                {bet_type}
                              </label>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                      </div>
                    </div>
                    </div>
                  ))}
                </div>
              ))) :
              (<div className='m-auto text-center mt-5'> 
              <p className='text-white'><em>No pending bets...</em></p>
              <img src = {empty} height="150px" alt='Empty...' />
              </div>)
            }  
            </div>
          </div>
        </div>      
      </div>
      )}
      {marketingPreviewOpen && (
        <div className='modal fade show' style={{ display: 'block', background: 'rgba(0,0,0,0.65)' }}>
          <div className='modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable'>
            <div className='modal-content'>
              <div className='modal-header bg-green text-white modal-head'>
                <h5 className='modal-title fw-bold'>Preview Marketing Email</h5>
                <button className='btn-close' onClick={() => setMarketingPreviewOpen(false)}></button>
              </div>
              <div className='modal-body bg-green text-white text-start'>
                <dl className='row mb-4'>
                  <dt className='col-sm-3'>From</dt>
                  <dd className='col-sm-9'>FantasyBet &lt;info@fantasybet.gg&gt;</dd>
                  <dt className='col-sm-3'>To</dt>
                  <dd className='col-sm-9'>{marketingRecipientCount} opted-in recipient{marketingRecipientCount === 1 ? '' : 's'}, sent individually</dd>
                  <dt className='col-sm-3'>Subject</dt>
                  <dd className='col-sm-9'>{marketingSubject.trim()}</dd>
                </dl>
                <div className='d-flex justify-content-center gap-2 mb-3'>
                  <button className={`btn btn-sm ${marketingPreviewDevice === 'desktop' ? 'btn-prim text-white' : 'btn-outline-light'}`} onClick={() => setMarketingPreviewDevice('desktop')}>
                    Desktop
                  </button>
                  <button className={`btn btn-sm ${marketingPreviewDevice === 'mobile' ? 'btn-prim text-white' : 'btn-outline-light'}`} onClick={() => setMarketingPreviewDevice('mobile')}>
                    Mobile
                  </button>
                </div>
                <div className='marketing-preview-stage rounded p-3'>
                  <div className={`marketing-email-preview ${marketingPreviewDevice}`}>
                    <div className='marketing-email-header'>
                      <img src='https://i.imgur.com/gYjUlWs.png' height='40' alt='FantasyBet' />
                    </div>
                    <div className='marketing-email-body'>
                      <h1>{marketingSubject.trim()}</h1>
                      <div
                        className='marketing-preview-content'
                        dangerouslySetInnerHTML={{ __html: cleanMarketingPreview }}
                      />
                      <hr />
                      <div className='small text-lightgrey'>
                        You received this because you opted in to FantasyBet marketing emails.<br />
                        <span className='text-orange text-decoration-underline'>Unsubscribe from marketing emails</span>
                      </div>
                    </div>
                  </div>
                </div>
                <div className={`alert ${marketingEnabled ? 'alert-warning' : 'alert-info'} mt-4 mb-0`}>
                  {marketingEnabled
                    ? 'Confirming starts delivery. A queued campaign cannot be recalled from this screen.'
                    : 'This is a preview only. Marketing email delivery is currently disabled.'}
                </div>
              </div>
              <div className='modal-footer bg-green border-top-0'>
                <button className='btn border text-white fw-bold' disabled={marketingLoading} onClick={() => setMarketingPreviewOpen(false)}>
                  Go back and edit
                </button>
                <button
                  className='btn btn-danger fw-bold'
                  disabled={marketingLoading || !marketingEnabled || !marketingConfigured || marketingRecipientCount === 0}
                  onClick={sendMarketingCampaign}
                  title={!marketingEnabled ? 'Marketing email delivery is disabled' : undefined}
                >
                  {marketingLoading ? 'Queuing...' : `Confirm and send to ${marketingRecipientCount}`}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {confirmAction && (
        
        <div className="modal fade show" style={{ display: "block", background: "rgba(0,0,0,0.5)" }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              
              <div className="modal-header bg-green text-white modal-head">
                <h5 className="modal-title fw-bold">
                  {confirmAction.type === 'save' ? (<>Confirm <span className='text-orange'>Save</span> Market</>) : (<>Confirm <span className='text-red'>Void</span> Market</>)}
                </h5>
                <button className="btn-close" onClick={() => setConfirmAction(null)}></button>
              </div>

              <div className="modal-body bg-green text-white">
                {confirmAction.type === 'save' ? (
                  <>
                    <div className='fw-bold'>{confirmAction.payload.fixture} - {confirmAction.payload.bet_market}</div>
                    <div>{formatLocalKickoff(confirmAction.payload.match_day, confirmAction.payload.time)}</div>

                    <div className="mt-3">
                      <strong>Selected Winning Bet Type:</strong>
                      <div className="text-orange">{winningList}</div>
                    </div>

                    <div className="mt-3">
                      Are you sure you want to <span className='fw-bold text-orange'>save</span> this market? <br />
                      This will settle the market and pay the winners.
                    </div>
                  </>
                ) : (
                  <>
                    <div className='fw-bold'>{confirmAction.payload.fixture} - {confirmAction.payload.bet_market}</div>
                    <div>{formatLocalKickoff(confirmAction.payload.match_day, confirmAction.payload.time)}</div>
                    <div className="mt-3">
                      Are you sure you want to <span className='fw-bold text-red'>void</span> this market? <br />
                      All bets will be refunded.
                    </div>
                  </>
                ) }
              </div>

              <div className="modal-footer bg-green border-top-0 justify-content-end pt-0 pb-3">
                <button className="mt-1 btn border fw-bold text-white p-2" onClick={() => setConfirmAction(null)}>Cancel</button>

                <button
                  className={`mt-1 btn text-white fw-bold p-2 ${confirmAction.type === 'save' ? 'btn-prim' : 'btn-danger'}`}
                  onClick={() => {
                    const { match_day, time, fixture, bet_market } = confirmAction.payload;
                    setConfirmAction(null);

                    if (confirmAction.type === 'save') {
                      submitMarketResults(match_day, time, fixture, bet_market);
                    } else {
                      voidMarket(match_day, time, fixture, bet_market);
                    }
                  }}
                >
                  Confirm
                </button>
              </div>

            </div>
          </div>
        </div>
      )}
      <div className='alertsBox'>
        {showAlert && (
          <div
            className={`alert ${
              alertText.includes("Error")
                ? "alert-danger"
                : "alert-success"
            } fade show ${animationClass}`}
            role="alert"
          >{toPlainText(alertText)}</div>
        )}
      </div>

    </div>
  );
}
  const toUtcKickoff = (matchDay, time = '12:00:00') =>
    new Date(`${matchDay}T${time}Z`);

  const formatLocalDate = (matchDay, time) =>
    toUtcKickoff(matchDay, time).toLocaleDateString(undefined, {
      day: 'numeric', month: 'long', year: 'numeric',
    });

  const formatLocalTime = (matchDay, time) =>
    toUtcKickoff(matchDay, time).toLocaleTimeString(undefined, {
      hour: '2-digit', minute: '2-digit', hour12: false,
    });

  const formatLocalKickoff = (matchDay, time) =>
    `${formatLocalDate(matchDay, time)} ${formatLocalTime(matchDay, time)}`;
